/* eslint-disable no-extra-parens */
import React, { memo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { reduxConnect } from '../../common';
import { LifeSubStory } from '../common/life/lifeSubStory';
import { smallMediaQuery, mediumMediaQuery, largeMediaQuery } from '../../common/utilities/sizeHelpers';
import { translate } from  '../../common/translation/translate';
import { numberOfAqiListItemsLoading } from './constants';
import { AirQualitySubStoryLoadingUI } from './v2/loadingUI/airQualitySubStoryLoadingUI';
import { DefaultAqiErrorUI } from './v2/noDataUI/defaultAqiErrorUI';
import { isDataError } from '../common/utilities/isDataError';
import { getNewsStories } from '../../common/actions/stories/storiesAction';

const source = 'air-quality';

const mapStoreStateToProps = storeState => {
    const appState = storeState.appState.appState || {};
    const { airQualityAttributionUrl, enableSourceParameterOnStoriesLinks } = appState;
    const { newsStories } =  storeState;

    return {
        airQualityStories: newsStories?.stories,
        airQualityAttributionUrl,
        isFetchingAirQualityStories: newsStories?.isFetchingNewsStories,
        lastErrorFetchingAirQualityStories: newsStories?.lastError,
        enableSourceParameterOnStoriesLinks
    };
};

const mapDispatchToProps = {
    getNewsStories
};

// eslint-disable-next-line react/display-name
export const AirQualityStories =  reduxConnect( mapStoreStateToProps, mapDispatchToProps ) (memo(({
    airQualityStories,
    airQualityAttributionUrl,
    isFetchingAirQualityStories,
    getNewsStories,
    lastErrorFetchingAirQualityStories,
    enableSourceParameterOnStoriesLinks
}) => {
    const failedToLoadStories = isDataError(lastErrorFetchingAirQualityStories);

    useEffect(() => {
        window.scrollTo(0, 0);
        getNewsStories({ baseUrl: location.origin, category: 'air-quality', count: 6 });
    }, []);

    const renderStoriesListUI = useCallback((storiesIsLoading, storiesList, storiesError) => {
        if (storiesIsLoading && !storiesList?.length) { // stories don't change frequently or change based on location show storiesIsLoading only when storiesList is empty
            const aqiStoriesLoadingItemToRender = numberOfAqiListItemsLoading(6); 
            return (
                aqiStoriesLoadingItemToRender.map((listItemNumber) => {
                    return <AirQualitySubStoryLoadingUI key={listItemNumber} />;
                })
            );
        } else if (storiesList?.length) {
            return (
                <>

                    {
                        storiesList.map(subStory => {
                            return (
                                <LifeSubStory
                                    key={subStory?.Id}
                                    subStory={subStory}
                                    enableSourceParameterOnStoriesLinks={enableSourceParameterOnStoriesLinks}
                                    source={source}
                                />
                            );
                        })
                    }
                </>
            );
        } else { 
            return <DefaultAqiErrorUI isStoriesUI={true} />;
        }
    }, []);

    return (
        <PageContainer>
            <Title>{translate('airQualityTitle')}</Title>
            <StoriesContainer>
                <SubStoryCardList isErrorViewOrLoadingView={isFetchingAirQualityStories || failedToLoadStories}>
                    {renderStoriesListUI(isFetchingAirQualityStories, airQualityStories, failedToLoadStories)}
                </SubStoryCardList>
            </StoriesContainer>
            <AirQualityAttributionContainer>
                <a href={airQualityAttributionUrl} rel='noopener noreferrer' target='_blank'>
                    <AttributionImage src='/images/airquality/AQ_by_Ambee.png'/>
                </a>
            </AirQualityAttributionContainer> 
        </PageContainer>
    );
}));

AirQualityStories.displayName = 'AirQualityStories';
AirQualityStories.propTypes = {
    airQualityStories: PropTypes.array,
    getNewsStories: PropTypes.func,
    isFetchingAirQualityStories: PropTypes.bool,
    airQualityAttributionUrl: PropTypes.string,
    openNavMenu: PropTypes.func
};

const PageContainer = styled.div`
    width: 100%;
`;

const Title = styled.div`
    display: flex;
    flex-direction: row;
    margin: 20px 0 5px 10px;
    font-size: 20px;
    font-weight: 600;

    @media ${smallMediaQuery} {
        justify-content: left;
    }
`;

const StoriesContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    @media ${mediumMediaQuery} {
        margin-bottom: 10px;
    }
`;

const SubStoryCardList = styled.ul`
    padding-left: 0;
    white-space: normal;

    @media ${mediumMediaQuery} {
        margin-right: -9px;
    }

    @media ${smallMediaQuery} {
        margin: 0;
    }

    ${({ isErrorViewOrLoadingView }) => isErrorViewOrLoadingView && `
        width: 100%;
        @media ${largeMediaQuery} {
            margin-right: 10px;
        }
    `}
`;

const AirQualityAttributionContainer = styled.div`
    display: flex; 
    margin-top: 10px; 
    flex-direction: column; 
    align-items: center; 
`;

const AttributionImage = styled.img`
    width: auto;
    height: 30px;
`;